import { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { PAYMENT_PROOF_ACCEPT, resolvePaymentProofUrl, validatePaymentProof } from '../paymentProof';

const PAYMENT_MODES = {
    CASH: 'Cash', CHEQUE: 'Cheque', DD: 'Demand Draft', BANK_TRANSFER: 'Bank Transfer',
    UPI: 'UPI', NEFT: 'NEFT', RTGS: 'RTGS', ONLINE: 'Online',
};

const COMPONENT_LABELS = {
    PRINCIPAL: 'Principal',
    INTEREST: 'Interest',
    ARREAR: 'Arrear',
    DEBIT_NOTE_ARREAR: 'Debit Note Arrear',
};

const ReceiptDetailModal = ({ receipt, glAccounts = [], loading, onClose, onPrint, onClear, onBounce, onCancel, clearLoading, bounceLoading, cancelLoading, onReplaceProof, onRemoveProof, onReloadProof, proofLoading }) => {
    const proofInputRef = useRef(null);
    const [proofImageFailed, setProofImageFailed] = useState(false);
    const proofUrl = resolvePaymentProofUrl(receipt?.payment_proof_view_url);

    useEffect(() => {
        setProofImageFailed(false);
    }, [proofUrl]);

    if (!receipt) return null;

    const formatCurrency = (amount) => `Rs ${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'CLEARED': return 'tw-bg-emerald-100 tw-text-emerald-700';
            case 'PENDING': return 'tw-bg-amber-100 tw-text-amber-700';
            case 'BOUNCED': return 'tw-bg-red-100 tw-text-red-700';
            case 'CANCELLED': return 'tw-bg-gray-200 tw-text-gray-600';
            default: return 'tw-bg-gray-100 tw-text-gray-700';
        }
    };

    const status = receipt.status || 'PENDING';
    const flat = receipt.flat || {};
    const entity = receipt.entity || {};
    const memberName = entity.entity_type === 'I'
        ? `${entity.first_name || ''} ${entity.last_name || ''}`.trim()
        : entity.company_name || '-';
    const wing = flat.wing?.title || '-';
    const unit = flat.flat_number || '-';
    const amount = parseFloat(receipt.amount || 0);
    const tdsAmount = parseFloat(receipt.tds_deducted || 0);
    // amount is what was banked; TDS the member withheld settles dues on top of it
    const totalSettled = amount + tdsAmount;
    // Resolve the stored GL code to its account name, falling back to the bare code
    const tdsGlLabel = receipt.tds_gl_code
        ? (glAccounts.find(a => a.ac_code === receipt.tds_gl_code)?.label || receipt.tds_gl_code)
        : '';
    const allocations = receipt.invoiceAllocations || [];
    const totalAllocated = allocations.reduce((sum, a) => sum + parseFloat(a.amount || 0), 0);
    const unallocated = totalSettled - totalAllocated;

    const canClear = status === 'PENDING';
    const canBounce = ['PENDING', 'CLEARED'].includes(status);
    const canCancel = ['PENDING', 'CLEARED'].includes(status);
    const canEditProof = Boolean(onReplaceProof) && status !== 'CANCELLED';

    const handleProofSelect = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        const error = validatePaymentProof(file);
        if (error) {
            alert(error);
            return;
        }
        onReplaceProof(file);
    };

    const getInsights = () => {
        const insights = [];
        if (status === 'BOUNCED') {
            insights.push({ type: 'warning', icon: 'mdi:alert-circle', text: 'This receipt has been marked as bounced.' });
        }
        if (status === 'CANCELLED') {
            insights.push({ type: 'warning', icon: 'mdi:close-circle', text: 'This receipt has been cancelled.' });
        }
        if (status === 'PENDING') {
            insights.push({ type: 'tip', icon: 'mdi:lightbulb-outline', text: 'This receipt is pending clearance. Clear it once payment is confirmed.' });
        }
        if (status === 'CLEARED') {
            insights.push({ type: 'success', icon: 'mdi:check-decagram', text: 'Payment has been cleared and applied.' });
        }
        if (unallocated > 0.01) {
            insights.push({ type: 'info', icon: 'mdi:information-outline', text: `${formatCurrency(unallocated)} remains unallocated.` });
        }
        if (tdsAmount > 0) {
            insights.push({ type: 'info', icon: 'mdi:percent', text: `TDS of ${formatCurrency(tdsAmount)} (${receipt.tds_rate || 0}% u/s ${receipt.tds_section || '-'}) deducted.` });
        }
        return insights;
    };

    const insights = getInsights();

    if (loading) {
        return (
            <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-4xl tw-p-8 tw-text-center">
                    <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-blue-600 tw-mx-auto"></div>
                    <p className="tw-text-gray-500 tw-mt-4">Loading receipt details...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
            <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-4xl tw-max-h-[90vh] tw-overflow-hidden tw-flex tw-flex-col">
                {/* Header */}
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b tw-border-gray-100">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-blue-500 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="mdi:receipt-text-outline" className="tw-text-white tw-text-xl" />
                        </div>
                        <div>
                            <h2 className="tw-text-lg tw-font-semibold tw-text-gray-800">{receipt.receipt_number || '-'}</h2>
                            <p className="tw-text-xs tw-text-gray-500">{receipt.receipt_type === 'PAYMENT' ? 'Payment Receipt' : receipt.receipt_type || 'Receipt'}</p>
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <span className={`tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium ${getStatusColor(status)}`}>{status}</span>
                        {onPrint && <button onClick={onPrint} className="tw-p-2 tw-rounded-lg hover:tw-bg-gray-100 tw-text-gray-400 hover:tw-text-purple-600 tw-transition-colors" title="Print Receipt"><Icon icon="mdi:printer-outline" className="tw-text-xl" /></button>}
                        <button onClick={onClose} className="tw-p-2 tw-rounded-lg hover:tw-bg-gray-100 tw-text-gray-400"><Icon icon="mdi:close" className="tw-text-xl" /></button>
                    </div>
                </div>

                {/* Body */}
                <div className="tw-flex-1 tw-overflow-y-auto tw-p-5">
                    <div className="tw-grid tw-grid-cols-3 tw-gap-5">
                        {/* Left Column */}
                        <div className="tw-col-span-2 tw-space-y-4">
                            {/* Member Info */}
                            <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4">
                                <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                    <Icon icon="mdi:account-outline" className="tw-text-gray-400" /> Received From
                                </h3>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                                    <div>
                                        <p className="tw-font-medium tw-text-gray-800">{memberName}</p>
                                        <p className="tw-text-xs tw-text-gray-500 tw-mt-1">{wing}-{unit} {flat.flat_type ? `| ${flat.flat_type}` : ''}</p>
                                        {entity.gstin_no && <p className="tw-text-xs tw-text-blue-600 tw-mt-1 tw-font-medium">GSTIN: {entity.gstin_no}</p>}
                                    </div>
                                    <div className="tw-text-right">
                                        {entity.phone && <p className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-justify-end tw-gap-1"><Icon icon="mdi:phone-outline" /> {entity.phone}</p>}
                                        {entity.email && <p className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-justify-end tw-gap-1 tw-mt-1"><Icon icon="mdi:email-outline" /> {entity.email}</p>}
                                    </div>
                                </div>
                            </div>

                            {/* Payment Details */}
                            <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4">
                                <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                    <Icon icon="mdi:credit-card-outline" className="tw-text-gray-400" /> Payment Details
                                </h3>
                                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-3">
                                    <div>
                                        <p className="tw-text-[11px] tw-text-gray-500">Payment Mode</p>
                                        <p className="tw-text-sm tw-font-semibold tw-text-gray-800">{PAYMENT_MODES[receipt.payment_mode] || receipt.payment_mode || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="tw-text-[11px] tw-text-gray-500">Payment Date</p>
                                        <p className="tw-text-sm tw-text-gray-700">{formatDate(receipt.payment_date)}</p>
                                    </div>
                                    {receipt.reference_no && (
                                        <div>
                                            <p className="tw-text-[11px] tw-text-gray-500">Reference No</p>
                                            <p className="tw-text-sm tw-font-medium tw-text-gray-800">{receipt.reference_no}</p>
                                        </div>
                                    )}
                                    {receipt.bank_name && (
                                        <div>
                                            <p className="tw-text-[11px] tw-text-gray-500">Bank Name</p>
                                            <p className="tw-text-sm tw-text-gray-700">{receipt.bank_name}</p>
                                        </div>
                                    )}
                                    {receipt.instrument_date && (
                                        <div>
                                            <p className="tw-text-[11px] tw-text-gray-500">Instrument Date</p>
                                            <p className="tw-text-sm tw-text-gray-700">{formatDate(receipt.instrument_date)}</p>
                                        </div>
                                    )}
                                </div>
                                {receipt.remarks && (
                                    <p className="tw-text-xs tw-text-gray-500 tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-200 tw-italic">Remarks: {receipt.remarks}</p>
                                )}
                            </div>

                            {/* Payment Proof - the view URL only comes from the detail endpoint */}
                            {(proofUrl || canEditProof) && (
                                <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4">
                                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-flex tw-items-center tw-gap-2 tw-mb-0">
                                            <Icon icon="mdi:image-outline" className="tw-text-gray-400" /> Payment Proof
                                        </h3>
                                        {canEditProof && (
                                            <div className="tw-flex tw-items-center tw-gap-2">
                                                <button type="button" onClick={() => proofInputRef.current?.click()} disabled={proofLoading} className="tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-border tw-border-blue-200 tw-text-blue-600 tw-rounded-md tw-text-xs tw-font-medium hover:tw-bg-blue-50 disabled:tw-opacity-50">
                                                    <Icon icon={proofLoading ? 'mdi:loading' : proofUrl ? 'mdi:image-edit-outline' : 'mdi:upload-outline'} className={proofLoading ? 'tw-animate-spin' : ''} />
                                                    {proofLoading ? 'Saving...' : proofUrl ? 'Replace' : 'Upload'}
                                                </button>
                                                {proofUrl && (
                                                    <button type="button" onClick={onRemoveProof} disabled={proofLoading} className="tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-border tw-border-red-200 tw-text-red-600 tw-rounded-md tw-text-xs tw-font-medium hover:tw-bg-red-50 disabled:tw-opacity-50">
                                                        <Icon icon="mdi:trash-can-outline" /> Remove
                                                    </button>
                                                )}
                                                <input ref={proofInputRef} type="file" accept={PAYMENT_PROOF_ACCEPT} onChange={handleProofSelect} hidden />
                                            </div>
                                        )}
                                    </div>
                                    {!proofUrl ? (
                                        <p className="tw-text-xs tw-text-gray-400 tw-mb-0">No proof photo attached.</p>
                                    ) : proofImageFailed ? (
                                        <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-500">
                                            <Icon icon="mdi:image-broken-variant" className="tw-text-base" />
                                            <span>The photo couldn't be loaded. Its link may have expired.</span>
                                            {onReloadProof && <button type="button" onClick={onReloadProof} className="tw-text-blue-600 tw-font-medium hover:tw-underline">Reload</button>}
                                        </div>
                                    ) : (
                                        <a href={proofUrl} target="_blank" rel="noreferrer" className="tw-inline-block" title="Open full size">
                                            <img src={proofUrl} alt="Payment proof" onError={() => setProofImageFailed(true)} className="tw-max-h-64 tw-max-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-object-contain" />
                                        </a>
                                    )}
                                </div>
                            )}

                            {/* TDS Details */}
                            {tdsAmount > 0 && (
                                <div className="tw-bg-indigo-50 tw-rounded-lg tw-p-4 tw-border tw-border-indigo-100">
                                    <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                        <Icon icon="mdi:percent" className="tw-text-indigo-500" /> TDS Details
                                    </h3>
                                    <div className="tw-grid tw-grid-cols-3 tw-gap-3">
                                        <div>
                                            <p className="tw-text-[11px] tw-text-gray-500">TDS Amount</p>
                                            <p className="tw-text-sm tw-font-semibold tw-text-gray-800">{formatCurrency(tdsAmount)}</p>
                                        </div>
                                        <div>
                                            <p className="tw-text-[11px] tw-text-gray-500">TDS Rate %</p>
                                            <p className="tw-text-sm tw-text-gray-700">{receipt.tds_rate || 0}%</p>
                                        </div>
                                        <div>
                                            <p className="tw-text-[11px] tw-text-gray-500">Section</p>
                                            <p className="tw-text-sm tw-text-gray-700">{receipt.tds_section || '-'}</p>
                                        </div>
                                        {receipt.tds_gl_code && (
                                            <div className="tw-col-span-3 tw-pt-2 tw-border-t tw-border-indigo-100">
                                                <p className="tw-text-[11px] tw-text-gray-500">GL Account</p>
                                                <p className="tw-text-sm tw-text-gray-700">{tdsGlLabel}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Allocations */}
                            <div className="tw-bg-white tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-hidden">
                                <div className="tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                    <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-flex tw-items-center tw-gap-2">
                                        <Icon icon="mdi:format-list-bulleted" className="tw-text-gray-400" /> Allocations
                                    </h3>
                                </div>
                                {allocations.length > 0 ? (
                                    <div className="tw-divide-y tw-divide-gray-100">
                                        {allocations.map((alloc, idx) => (
                                            <div key={idx} className="tw-px-4 tw-py-3">
                                                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                                                    <div className="tw-flex tw-items-center tw-gap-2">
                                                        <Icon icon={alloc.invoice_id ? 'mdi:file-document-outline' : 'mdi:note-minus-outline'} className={`tw-text-base ${alloc.invoice_id ? 'tw-text-blue-500' : 'tw-text-orange-500'}`} />
                                                        <div>
                                                            <span className="tw-text-sm tw-font-medium tw-text-gray-800">
                                                                {alloc.invoice_id ? `Invoice ${alloc.invoice?.document_number || `#${alloc.invoice_id}`}` : `Debit Note ${alloc.debitNote?.document_number || `#${alloc.debit_note_id}`}`}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <span className="tw-text-sm tw-font-semibold tw-text-gray-800">{formatCurrency(alloc.amount)}</span>
                                                </div>
                                                {alloc.components && alloc.components.length > 0 && (
                                                    <div className="tw-ml-6 tw-mt-1 tw-space-y-1">
                                                        {alloc.components.map((comp, cIdx) => (
                                                            <div key={cIdx} className="tw-flex tw-items-center tw-justify-between tw-text-xs">
                                                                <span className="tw-text-gray-500 tw-flex tw-items-center tw-gap-1">
                                                                    <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-gray-300"></span>
                                                                    {COMPONENT_LABELS[comp.component] || comp.component}
                                                                </span>
                                                                <span className="tw-text-gray-700 tw-font-medium">{formatCurrency(comp.amount)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="tw-px-4 tw-py-6 tw-text-center tw-text-gray-400 tw-text-sm">
                                        No allocations found. This may be an advance receipt.
                                    </div>
                                )}
                                {allocations.length > 0 && (
                                    <div className="tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-t tw-border-gray-100">
                                        <div className="tw-flex tw-items-center tw-justify-between tw-text-sm">
                                            <span className="tw-font-medium tw-text-gray-600">Total Allocated</span>
                                            <span className="tw-font-bold tw-text-gray-800">{formatCurrency(totalAllocated)}</span>
                                        </div>
                                        {unallocated > 0.01 && (
                                            <div className="tw-flex tw-items-center tw-justify-between tw-text-sm tw-mt-1">
                                                <span className="tw-font-medium tw-text-amber-600">Unallocated</span>
                                                <span className="tw-font-bold tw-text-amber-600">{formatCurrency(unallocated)}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right Column */}
                        <div className="tw-space-y-4">
                            {/* Amount Summary */}
                            <div className="tw-bg-gradient-to-br tw-from-blue-500 tw-to-blue-600 tw-rounded-lg tw-p-4 tw-text-white">
                                <p className="tw-text-white/70 tw-text-xs tw-mb-1">Amount Received (Bank)</p>
                                <p className="tw-text-2xl tw-font-bold">{formatCurrency(amount)}</p>
                                {tdsAmount > 0 && (
                                    <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-white/20 tw-space-y-2">
                                        <div className="tw-flex tw-justify-between tw-text-sm">
                                            <span className="tw-text-white/70">TDS Deducted</span>
                                            <span className="tw-font-medium">+ {formatCurrency(tdsAmount)}</span>
                                        </div>
                                        <div className="tw-flex tw-justify-between tw-text-sm">
                                            <span className="tw-text-white/70">Total Settled</span>
                                            <span className="tw-font-medium">{formatCurrency(totalSettled)}</span>
                                        </div>
                                    </div>
                                )}
                                {allocations.length > 0 && (
                                    <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-white/20">
                                        <div className="tw-flex tw-justify-between tw-text-xs tw-mb-1">
                                            <span className="tw-text-white/70">Allocated</span>
                                            <span>{Math.round((totalAllocated / amount) * 100)}%</span>
                                        </div>
                                        <div className="tw-w-full tw-bg-white/20 tw-rounded-full tw-h-2">
                                            <div className="tw-bg-white tw-h-2 tw-rounded-full tw-transition-all" style={{ width: `${Math.min((totalAllocated / amount) * 100, 100)}%` }}></div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Date & Info */}
                            <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-space-y-3">
                                <div className="tw-flex tw-items-center tw-justify-between">
                                    <span className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-1"><Icon icon="mdi:calendar-outline" /> Payment Date</span>
                                    <span className="tw-text-sm tw-font-medium tw-text-gray-800">{formatDate(receipt.payment_date)}</span>
                                </div>
                                <div className="tw-flex tw-items-center tw-justify-between">
                                    <span className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-1"><Icon icon="mdi:credit-card-outline" /> Mode</span>
                                    <span className="tw-text-sm tw-font-medium tw-text-gray-800">{PAYMENT_MODES[receipt.payment_mode] || receipt.payment_mode}</span>
                                </div>
                                <div className="tw-flex tw-items-center tw-justify-between">
                                    <span className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-1"><Icon icon="mdi:tag-outline" /> Type</span>
                                    <span className="tw-text-sm tw-font-medium tw-text-gray-800">{receipt.receipt_type === 'PAYMENT' ? 'Payment' : receipt.receipt_type || '-'}</span>
                                </div>
                                {receipt.society && (
                                    <div className="tw-flex tw-items-center tw-justify-between">
                                        <span className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-1"><Icon icon="mdi:office-building-outline" /> Society</span>
                                        <span className="tw-text-xs tw-font-medium tw-text-gray-800">{receipt.society.name}</span>
                                    </div>
                                )}
                            </div>

                            {/* Insights */}
                            {insights.length > 0 && (
                                <div className="tw-bg-gradient-to-br tw-from-purple-50 tw-to-indigo-50 tw-rounded-lg tw-p-4 tw-border tw-border-purple-100">
                                    <h3 className="tw-text-sm tw-font-semibold tw-text-purple-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                        <Icon icon="mdi:robot-outline" className="tw-text-purple-500" /> Insights
                                    </h3>
                                    <div className="tw-space-y-2">
                                        {insights.map((insight, idx) => (
                                            <div key={idx} className={`tw-flex tw-items-start tw-gap-2 tw-text-xs tw-p-2 tw-rounded-md ${
                                                insight.type === 'warning' ? 'tw-bg-amber-50 tw-text-amber-800' :
                                                insight.type === 'success' ? 'tw-bg-emerald-50 tw-text-emerald-800' :
                                                insight.type === 'tip' ? 'tw-bg-blue-50 tw-text-blue-800' :
                                                'tw-bg-gray-50 tw-text-gray-700'
                                            }`}>
                                                <Icon icon={insight.icon} className="tw-text-base tw-flex-shrink-0 tw-mt-0.5" />
                                                <span>{insight.text}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Quick Actions */}
                            <div className="tw-space-y-2">
                                {canClear && (
                                    <button onClick={onClear} disabled={clearLoading} className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-bg-emerald-600 tw-text-white tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-emerald-700 tw-transition-colors disabled:tw-opacity-50">
                                        {clearLoading ? (<><div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-b-2 tw-border-white"></div> Clearing...</>) : (<><Icon icon="mdi:check-circle" /> Clear Receipt</>)}
                                    </button>
                                )}
                                {canBounce && (
                                    <button onClick={onBounce} disabled={bounceLoading} className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-border tw-border-orange-200 tw-text-orange-600 tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-orange-50 tw-transition-colors disabled:tw-opacity-50">
                                        {bounceLoading ? (<><div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-b-2 tw-border-orange-600"></div> Bouncing...</>) : (<><Icon icon="mdi:alert-circle" /> Bounce Receipt</>)}
                                    </button>
                                )}
                                {canCancel && (
                                    <button onClick={onCancel} disabled={cancelLoading} className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-border tw-border-red-200 tw-text-red-600 tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-red-50 tw-transition-colors disabled:tw-opacity-50">
                                        {cancelLoading ? (<><div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-b-2 tw-border-red-600"></div> Cancelling...</>) : (<><Icon icon="mdi:close-circle" /> Cancel Receipt</>)}
                                    </button>
                                )}
                                <button onClick={onClose} className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-border tw-border-gray-200 tw-text-gray-700 tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-gray-50 tw-transition-colors">
                                    <Icon icon="mdi:close" /> Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReceiptDetailModal;
